import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { GlobalProps } from '@bloomberg/media-ui-core';
import { classes, useSlug } from '@bloomberg/media-ui-core';
import { SignInModal } from '@bloomberg/media-ui-login';
import type MediaServicesSearchClient from '@foundation/media-services-search-client/lib/index';
import RegUIClient from 'reg-ui-client';

import { AuthorSession } from '../AuthorSession/index';
import { BreakingNews } from '../BreakingNews/index';
import { BreakingNewsLiveStream } from '../BreakingNewsLiveStream/index';
import { MOBILE_NAV_WIDTH } from '../constants/constants';
import { LogoBar, SignInButtons } from '../LogoBar/index';
import { MenuDesktop } from '../MenuDesktop/index';
import NavMobile, { type MobileMenuHandle } from '../NavMobile';
import {
  BannerData,
  LANGUAGES,
  MenuItem,
  MenuItems,
  NavTips,
  REGIONS,
  THEMES,
  User,
} from '../types';
import { getExpPrefCookie } from '../utils/expPref';

import { NavProvider } from './NavContext';

import styles from './Nav.module.css';

export type NavProps = GlobalProps & {
  bannerData?: BannerData;
  desktopBreakpointClassName?: string;
  initializeSignInWithRegUI: (signInButtons: SignInButtons) => void;
  onSignOut: () => void;
  isWeekend?: boolean;
  language?: LANGUAGES;
  marketDataTickersUrl?: string;
  name?: string;
  locale?: string;
  region?: REGIONS;
  countryCode?: string;
  searchClient: MediaServicesSearchClient;
  theme?: THEMES;
  user?: User;
  navTips?: NavTips;
  useSubmenu?: boolean;
  trackingFunction?: (eventAction: string, eventLabel: string) => void;
  bannersTrackingFunction?: (eventAction: string, eventLabel: string) => void;
  subscriptionUrl: string;
  upgradeUrl: string;
  regUIClient?: RegUIClient;
  handleShowSignInModal?: () => void;
  setSignInButtonRef?: Dispatch<SetStateAction<SignInButtons | undefined>>;
  disableOnboarding?: boolean;
} & (
    | {
        hideMenu?: false;
        menuItems: MenuItems;
      }
    | {
        hideMenu: true;
        menuItems?: MenuItems;
      }
  );

export type NavDesktopProps = Omit<
  NavProps,
  'menuItems' | 'trackingFunction'
> & {
  menuItems: Record<string, MenuItem>;
  trackingFunction: (action: string, eventLabel: string) => void;
  handleShowSignInModal?: () => void;
  setSignInButtonRef?: Dispatch<SetStateAction<SignInButtons | undefined>>;
};

export const DEFAULT_USER: User = {
  isLoggedIn: false,
  isSubscriber: false,
};

export const Nav = React.forwardRef<HTMLDivElement, NavProps>(
  function NavComponent(props: NavProps, ref) {
    const {
      /* eslint-disable @typescript-eslint/no-unused-vars */
      bannerData = {},
      menuItems = { menuItemsDesktop: {}, menuItemsMobile: {} },
      name = 'nav',
      locale = 'en',
      region = REGIONS.US,
      countryCode,
      theme = THEMES.LIGHT,
      trackingFunction = () => {},
      bannersTrackingFunction = () => {},
      initializeSignInWithRegUI = () => {},
      desktopBreakpointClassName,
      regUIClient,
      disableOnboarding = false,
      /* eslint-enable @typescript-eslint/no-unused-vars */
    } = props;

    const [selectedRegion, setSelectedRegion] = useState<REGIONS>(region);
    const mobileNavRef = useRef<MobileMenuHandle>(null);

    const [showModal, setShowModal] = useState(false);
    const [signInIframeRef, setSignInButtonRef] = useState<SignInButtons>();
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
      const exp_pref_region = getExpPrefCookie();

      if (exp_pref_region) {
        setSelectedRegion(exp_pref_region as REGIONS);
      }
    }, []);

    useEffect(() => {
      // SignInModal assumes application has been mounted, and adds a dialog to `document`
      setIsMounted(locale === 'en');
    }, []);

    const handleResize = () => {
      if (window.innerWidth > MOBILE_NAV_WIDTH) {
        document.body.style.overflowY = 'auto';

        return;
      }

      if (mobileNavRef?.current?.getIsMenuOpen()) {
        document.body.style.overflowY = 'hidden';
      }
    };

    useEffect(() => {
      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, []);

    const componentName = useSlug(name);
    const { menuItemsDesktop, menuItemsMobile } = menuItems;

    const cx = classes(
      [
        styles.navContainer,
        {
          [styles.light]: theme === THEMES.LIGHT,
          [styles.dark]: theme === THEMES.DARK,
          [styles.blue]: theme === THEMES.BLUE,
        },
      ],
      {
        name,
      }
    );

    const onClick = useCallback(
      (eventAction: string, eventLabel: string) => {
        trackingFunction(eventAction, eventLabel);
      },
      [trackingFunction]
    );

    const handleShowSignInModal = () => {
      setShowModal(true);
    };

    const initializeSignInIframe = () => {
      // Note: this function and the same function on NavMinimal are identical
      if (!isMounted || !signInIframeRef) {
        return;
      }

      if (
        !signInIframeRef.mobileSignInButton &&
        !signInIframeRef.desktopSignInButton
      ) {
        // eslint-disable-next-line no-console
        console.error('There is no signInIframeRef button!');

        return;
      }

      initializeSignInWithRegUI(signInIframeRef);
    };

    return (
      <NavProvider locale={locale} region={region}>
        <nav className={cx} ref={ref} data-component={componentName}>
          {isMounted && (
            <SignInModal
              signInClicked={showModal}
              setSignInClicked={setShowModal}
              countryCode={countryCode}
              regUIClient={regUIClient}
              enableOnboarding={!disableOnboarding}
              initializeSignInIframe={initializeSignInIframe}
            />
          )}
          <NavDesktop
            {...props}
            region={selectedRegion}
            trackingFunction={onClick}
            menuItems={menuItemsDesktop}
            handleShowSignInModal={handleShowSignInModal}
            setSignInButtonRef={setSignInButtonRef}
          />
          <div className={styles.mobile}>
            <NavMobile
              {...props}
              ref={mobileNavRef}
              region={selectedRegion}
              menuItems={menuItemsMobile}
              trackingFunction={onClick}
              handleShowSignInModal={handleShowSignInModal}
              setSignInButtonRef={setSignInButtonRef}
            />
          </div>
          <Banners
            bannerData={bannerData}
            desktopBreakpointClassName={desktopBreakpointClassName}
            trackingFunction={bannersTrackingFunction}
          />
        </nav>
      </NavProvider>
    );
  }
);

function NavDesktop(props: NavDesktopProps) {
  const {
    desktopBreakpointClassName,
    hideMenu,
    onSignOut,
    isWeekend,
    region = REGIONS.US,
    searchClient,
    theme,
    user,
    trackingFunction,
    countryCode,
    subscriptionUrl,
    upgradeUrl,
    handleShowSignInModal,
    setSignInButtonRef,
    navTips,
  } = props;

  const [isUserOptionsDropdownOpen, setIsUserOptionsDropdownOpen] =
    useState(false);

  const handleOnMenuItemActive = () => {
    setIsUserOptionsDropdownOpen(false);
  };

  return (
    <div className={styles.desktop}>
      <LogoBar
        region={region}
        isUserOptionsDropdownOpen={isUserOptionsDropdownOpen}
        setIsUserOptionsDropdown={(isOpen: boolean) =>
          setIsUserOptionsDropdownOpen(isOpen)
        }
        isWeekend={isWeekend}
        searchClient={searchClient}
        theme={theme}
        user={user}
        desktopBreakpointClassName={desktopBreakpointClassName}
        onSignOut={onSignOut}
        trackingFunction={trackingFunction}
        countryCode={countryCode}
        subscriptionUrl={subscriptionUrl}
        upgradeUrl={upgradeUrl}
        tips={navTips}
        handleShowSignInModal={handleShowSignInModal}
        setSignInButtonRef={setSignInButtonRef}
      />
      {!hideMenu && (
        <MenuDesktop
          {...props}
          onMenuItemActive={handleOnMenuItemActive}
          region={region}
        />
      )}
    </div>
  );
}

function Banners({
  bannerData,
  desktopBreakpointClassName,
  trackingFunction,
}: {
  bannerData: BannerData;
  desktopBreakpointClassName?: string;
  trackingFunction?: (eventAction: string, eventLabel: string) => void;
}) {
  const {
    regionalAuthorSession,
    globalAuthorSession,
    regionalBreakingNews,
    globalBreakingNews,
    regionalBreakingNewsLiveStream,
    globalBreakingNewsLiveStream,
  } = bannerData;

  if (
    !regionalAuthorSession &&
    !globalAuthorSession &&
    !regionalBreakingNews &&
    !globalBreakingNews &&
    !regionalBreakingNewsLiveStream &&
    !globalBreakingNewsLiveStream
  ) {
    return null;
  }

  return (
    <div className={styles.banners}>
      {regionalBreakingNews && (
        <BreakingNews
          {...regionalBreakingNews}
          desktopBreakpointClassName={desktopBreakpointClassName}
          trackingFunction={trackingFunction}
        />
      )}
      {globalBreakingNews && (
        <BreakingNews
          {...globalBreakingNews}
          desktopBreakpointClassName={desktopBreakpointClassName}
          trackingFunction={trackingFunction}
        />
      )}
      {regionalAuthorSession && (
        <AuthorSession
          {...regionalAuthorSession}
          desktopBreakpointClassName={desktopBreakpointClassName}
          trackingFunction={trackingFunction}
        />
      )}
      {globalAuthorSession && (
        <AuthorSession
          {...globalAuthorSession}
          desktopBreakpointClassName={desktopBreakpointClassName}
          trackingFunction={trackingFunction}
        />
      )}
      {regionalBreakingNewsLiveStream && (
        <BreakingNewsLiveStream
          {...regionalBreakingNewsLiveStream}
          desktopBreakpointClassName={desktopBreakpointClassName}
          trackingFunction={trackingFunction}
        />
      )}
      {globalBreakingNewsLiveStream && (
        <BreakingNewsLiveStream
          {...globalBreakingNewsLiveStream}
          desktopBreakpointClassName={desktopBreakpointClassName}
          trackingFunction={trackingFunction}
        />
      )}
    </div>
  );
}
